// Task 8: approval cards for the Facebook voucher posts (4 slots a day). Same flow as PublishingApprovalPanel:
// the owner reviews images + caption + links, then approves or drops the slot. Studio only writes approval.json /
// caption.json / the autopost flag; the host runner applies them to post.json within a few seconds.
import { useCallback, useEffect, useState } from 'react';
import {
  decideVoucherDraft, fetchVoucherDrafts, saveVoucherCaption, setVoucherAutopost, type VoucherDraft,
} from '../services/affiliateHarnessClient';

const REASON_VI: Record<string, string> = {
  capture_failed: 'Không lấy được dữ liệu voucher từ Shopee',
  no_link: 'Không có liên kết tiếp thị để đăng',
  render_failed: 'Không tạo được ảnh bài đăng',
  phone_busy: 'Điện thoại đang bận nên không kịp lấy dữ liệu',
  shopee_blocked: 'Shopee yêu cầu xác minh, cần người xử lý',
  link_worker: 'Không chuyển được liên kết tiếp thị',
  link_worker_paused: 'Công cụ chuyển liên kết đang tạm dừng',
  compose_timeout: 'Quá hạn soạn bài',
  runner_busy: 'Máy chủ đang bận việc khác nên không kịp chạy',
  owner_skip: 'Bạn đã bỏ khung giờ này',
  expired: 'Hết hạn chờ duyệt, bài không được đăng',
  autopost_stale: 'Quá giờ đăng tự động, bài không đăng trễ',
  claude_not_run: 'Claude chưa chạy được suốt 90 phút, chưa bấm gì trên Facebook',
  facebook_failed: 'Facebook đăng không thành công',
  facebook_needs_human: 'Facebook cần người xử lý',
  facebook_queue_lost: 'Lệnh đăng bị thất lạc',
};
const FB_NOTE: Record<string, string> = {
  queued: 'Đã gửi lệnh đăng sang máy, đang đăng lên Facebook',
  failed: 'Đăng lên Facebook chưa được, hệ thống sẽ thử lại',
  needs_human: 'Facebook cần người kiểm tra thủ công',
};
// post.json approvalIgnored (set by the host's tick): why an approve/skip from Studio was not applied
const IGNORED_VI: Record<string, string> = {
  expired: 'Bản nháp đã hết hạn',
  too_late: 'Không bỏ kịp — bài đã vào hàng đăng',
};

const vnTime = (iso: string | null) => {
  if (!iso) return '—';
  const t = Date.parse(/(Z|[+-]\d{2}:?\d{2})$/i.test(iso) ? iso : `${iso}+07:00`);
  return Number.isFinite(t) ? new Date(t).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh', hour12: false, hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : iso;
};
// post.updatedAt is the capture time as "HH:MM"; accept a full ISO timestamp too
const hhmm = (v: string | null) => {
  if (!v) return '—';
  if (/^\d{1,2}:\d{2}$/.test(v)) return v;
  const t = Date.parse(v);
  return Number.isFinite(t) ? new Date(t).toLocaleTimeString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh', hour12: false, hour: '2-digit', minute: '2-digit' }) : v;
};
const slotLabel = (slot: string) => `${slot.slice(0, 2)}:${slot.slice(2)}`;
const dayLabel = (day: string) => day.split('-').reverse().join('/');

interface View { label: string; color: string }
function viewOf(d: VoucherDraft): View {
  const chili = 'var(--studio-chili)', grey = '#888';
  switch (d.status) {
    case 'pending_approval':
      if (d.approvalPending === 'approve') return { label: 'đã duyệt — đang chờ đăng', color: 'var(--studio-gold)' };
      if (d.approvalPending === 'skip') return { label: 'đang bỏ khung giờ này', color: grey };
      if (d.expired) return { label: 'hết hạn', color: grey };
      return { label: 'chờ duyệt', color: 'var(--studio-saffron)' };
    case 'approved':
      if (d.approvalPending === 'skip') return { label: 'đang bỏ khung giờ này', color: grey };
      switch (d.facebook.status) {
        case 'queued': return { label: 'đang đăng', color: 'var(--studio-terracotta)' };
        case 'failed': return { label: 'Facebook lỗi, đang thử lại', color: chili };
        case 'needs_human': return { label: 'Facebook cần anh xử lý', color: chili };
        case 'posted': return { label: 'đã đăng', color: '#2e7d32' };
        default: return { label: 'đã duyệt — đang chờ đăng', color: 'var(--studio-gold)' };
      }
    case 'posted': return { label: 'đã đăng', color: '#2e7d32' };
    case 'skipped': return { label: 'đã bỏ', color: grey };
    case 'expired': return { label: 'hết hạn', color: grey };
    case 'failed': return { label: 'lỗi', color: chili };
    default: return { label: d.status ?? '—', color: grey };
  }
}
const reasonText = (d: VoucherDraft) => {
  if (!d.reason) return '';
  const vi = REASON_VI[d.reason];
  return vi ? vi : d.reason;
};

const box: React.CSSProperties = { border: '1px solid #ddd', borderRadius: 8, padding: 12, display: 'grid', gap: 8 };

function VoucherCard({ d, busy, onDecide, onSave }: {
  d: VoucherDraft; busy: boolean;
  onDecide: (d: VoucherDraft, action: 'approve' | 'skip') => Promise<void>;
  onSave: (d: VoucherDraft, caption: string) => Promise<boolean>;
}) {
  const shown = d.captionDraft ?? d.caption;
  const [text, setText] = useState(shown);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { if (!dirty) setText(shown); }, [shown, dirty]);

  const v = viewOf(d);
  const waiting = d.status === 'pending_approval' && !d.approvalPending && !d.expired;
  // mirrors the server's autopostDroppable: approved by autopost, the poster has not touched it (no status, no attempts)
  const dropAutopost = d.status === 'approved' && d.approvedBy === 'autopost' && !d.facebook.status && !(d.facebook.attempts > 0) && !d.approvalPending;
  const images = [...d.images.post, ...(d.images.story ? [d.images.story] : [])];

  const save = async () => { if (await onSave(d, text)) setDirty(false); };
  const approve = async () => {
    if (dirty && !(await onSave(d, text))) return; // never approve over an unsaved edit
    setDirty(false);
    await onDecide(d, 'approve');
  };
  const skip = async () => {
    if (!window.confirm(`Bỏ khung giờ ${slotLabel(d.slot)} ngày ${dayLabel(d.day)}? Bài sẽ không được đăng.`)) return;
    await onDecide(d, 'skip');
  };

  return (
    <div style={box}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <strong>Voucher {slotLabel(d.slot)} · {dayLabel(d.day)}</strong>
        <span style={{ color: v.color }}>● {v.label}</span>
        {d.status === 'posted' && d.facebook.postUrl && <a href={d.facebook.postUrl} target="_blank" rel="noreferrer">xem bài đã đăng</a>}
        {d.autopost && <span title="Bản nháp này được soạn khi Tự đăng đang bật">⏱ tự đăng</span>}
        <span style={{ marginLeft: 'auto', fontSize: 13, color: '#555' }}>cập nhật lúc {hhmm(d.updatedAt)} · hết hạn duyệt {vnTime(d.expiresAt)}</span>
      </div>
      {['skipped', 'failed', 'expired'].includes(d.status ?? '') && (d.reason || d.detail) && (
        <div style={{ color: d.status === 'failed' ? 'var(--studio-chili)' : '#6d5b3f' }}>
          {reasonText(d)}{d.detail && d.detail !== d.reason ? <small style={{ display: 'block', color: '#888' }}>{d.detail}</small> : null}
        </div>
      )}
      {d.approvalIgnored && <div style={{ color: '#6d5b3f' }}>{IGNORED_VI[d.approvalIgnored] ?? d.approvalIgnored}</div>}
      {images.length > 0 && (
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
          {images.map((im, i) => (
            <a key={im.name} href={im.url} target="_blank" rel="noreferrer" style={{ flex: '0 0 auto' }} title={i === d.images.post.length ? 'Story' : im.name}>
              <img src={im.url} alt={i === d.images.post.length ? 'Story' : `Ảnh ${i + 1}`} loading="lazy" style={{ height: 180, borderRadius: 6, background: '#eee', display: 'block' }} />
            </a>
          ))}
        </div>
      )}
      {waiting ? (
        <textarea value={text} onChange={e => { setText(e.target.value); setDirty(true); }} rows={10} disabled={busy}
          style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', padding: 8, borderRadius: 6, border: '1px solid #ddd', background: '#faf7f2' }} />
      ) : (
        <pre style={{ whiteSpace: 'pre-wrap', margin: 0, background: '#faf7f2', padding: 8, borderRadius: 6 }}>{shown}</pre>
      )}
      {waiting && d.captionPending && !dirty && <div style={{ fontSize: 13, color: '#6d5b3f' }}>Caption đã lưu, đang chờ áp dụng.</div>}
      {waiting && d.captionRejected && !dirty && <div style={{ fontSize: 13, color: 'var(--studio-chili)' }}>Caption đã lưu bị từ chối (thiếu liên kết hoặc dòng "Có liên kết tiếp thị").</div>}
      {Object.keys(d.links).length > 0 && (
        <div style={{ fontSize: 13, display: 'grid', gap: 2 }}>
          {Object.entries(d.links).map(([k, url]) => <div key={k}><code>{k}</code> <a href={url} target="_blank" rel="noreferrer">{url}</a></div>)}
        </div>
      )}
      {d.status === 'approved' && d.facebook.status && FB_NOTE[d.facebook.status] && (
        <div style={{ fontSize: 13, color: '#555' }}>{FB_NOTE[d.facebook.status]}{d.facebook.attempts ? ` (lần ${d.facebook.attempts})` : ''}</div>
      )}
      {(waiting || dropAutopost) && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {waiting && <button disabled={busy} onClick={approve}>Duyệt</button>}
          <button disabled={busy} onClick={skip}>Bỏ khung giờ này</button>
          {waiting && <button disabled={busy || !dirty} onClick={save}>Lưu caption</button>}
        </div>
      )}
    </div>
  );
}

export default function VoucherApprovalPanel() {
  const [drafts, setDrafts] = useState<VoucherDraft[]>([]);
  const [autopost, setAutopost] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetchVoucherDrafts();
      setDrafts(r.drafts); setAutopost(r.autopost); setError('');
    } catch (e: any) { setError(e?.response?.data?.error ?? String(e)); } finally { setLoaded(true); }
  }, []);
  useEffect(() => { void load(); const t = setInterval(load, 30_000); return () => clearInterval(t); }, [load]);

  const decide = async (d: VoucherDraft, action: 'approve' | 'skip') => {
    setBusy(`${d.day}${d.slot}`);
    try { await decideVoucherDraft(d.day, d.slot, action); setError(''); } catch (e: any) { setError(e?.response?.data?.error ?? String(e)); } finally { setBusy(''); }
    await load();
  };
  const save = async (d: VoucherDraft, caption: string) => {
    setBusy(`${d.day}${d.slot}`);
    try { await saveVoucherCaption(d.day, d.slot, caption); setError(''); await load(); return true; }
    catch (e: any) { setError(e?.response?.data?.error ?? String(e)); return false; } finally { setBusy(''); }
  };
  const toggleAutopost = async () => {
    if (!autopost && !window.confirm('Bật Tự đăng? Các bản nháp soạn từ giờ sẽ tự đăng đúng giờ, không cần duyệt.')) return;
    try { setAutopost(await setVoucherAutopost(!autopost)); setError(''); } catch (e: any) { setError(e?.response?.data?.error ?? String(e)); }
  };

  return (
    <div style={{ padding: 16, display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Duyệt bài voucher Shopee</h2>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={autopost} onChange={toggleAutopost} /> Tự đăng
        </label>
        <small style={{ color: '#555' }}>Bật: bài tự đăng đúng giờ, không cần duyệt</small>
      </div>
      {error && <div style={{ color: 'var(--studio-chili)' }}>{error}</div>}
      {loaded && !drafts.length && !error && <div>Chưa có bản nháp voucher nào hôm nay hoặc hôm qua.</div>}
      {drafts.map(d => <VoucherCard key={`${d.day}-${d.slot}`} d={d} busy={busy === `${d.day}${d.slot}`} onDecide={decide} onSave={save} />)}
    </div>
  );
}
